/**
 * Footprint bars: volume at price, split by who was the aggressor.
 *
 * A candle says where price went. A footprint says what it cost to get there —
 * how much was lifted at each price and how much was hit — which is the
 * difference between "it went up" and "it went up on eleven contracts".
 *
 * ## The four decisions in here that are easy to get plausibly wrong
 *
 * **1. An imbalance is diagonal, not vertical.** The convention every platform
 * uses compares ask volume at a price against bid volume ONE TICK BELOW it,
 * because that is the pair of orders that actually competed: a buyer lifting
 * the offer at P is trading against the seller who could have hit the bid at
 * P − 1 tick. Comparing the two sides at the same price compares a buyer and a
 * seller who were never in the same queue, and produces a chart that looks
 * right and means nothing.
 *
 * **2. Unclassified prints are not split.** A print at the midpoint, or from a
 * venue that does not report aggressor, genuinely cannot be assigned. Putting
 * it on a side — or half on each — puts a number into delta that nobody
 * measured, and delta is the figure this whole chart is read for. They are
 * counted separately and reported, so a feed that classifies nothing produces
 * a visibly empty footprint rather than a symmetric-looking one.
 *
 * **3. The tick grid comes from the instrument master.** CLAUDE.md, and it
 * matters more here than almost anywhere: a footprint bucketed on the wrong
 * tick size merges two price levels into one, or splits one across two, and
 * every imbalance computed from it is against the wrong neighbour.
 *
 * A row may span SEVERAL ticks — `ticksPerRow` — and that is not a violation
 * of the same rule but a consequence of it. One tick of ES is a quarter of a
 * point and a bar holds thirty rows; one tick of BTCUSD is a cent and an hour
 * spans three hundred thousand of them. A single row per tick is a readable
 * chart for one asset class and an unusable one for the rest, and AEGIS trades
 * more than one (`docs/16`). Grouping is a rendering decision made ON TOP of
 * the instrument's real tick size, never a substitute for knowing it.
 *
 * **4. The time grid comes from the bars, not from the epoch.** A footprint
 * column IS a chart bar with its interior shown, and the two are read stacked
 * on top of each other. Flooring a timestamp to a multiple of the bar length
 * looks equivalent and is not: real bar grids are session-aligned, so a
 * five-minute bar can open at 09:32:00, and epoch-flooring then splits it
 * across two columns that line up with no candle on the chart. So the caller
 * passes the bar opens it is actually drawing, and every column returned here
 * carries one of them.
 */
import { Decimal, dec } from '../money.js';
import type { TapePrint } from '../marketdepth/index.js';

/** One price row inside a footprint bar. */
export interface FootprintLevel {
  /** The price, snapped to the instrument's tick grid. */
  readonly price: string;
  /** Volume that hit the bid — sellers were the aggressor. */
  readonly bidVolume: number;
  /** Volume that lifted the ask — buyers were the aggressor. */
  readonly askVolume: number;
  /** `askVolume - bidVolume`. Positive means buyers paid up here. */
  readonly delta: number;
  /** Everything classified at this price. */
  readonly total: number;
}

export interface FootprintImbalance {
  readonly price: string;
  /** Which side dominated the diagonal pair. */
  readonly side: 'buy' | 'sell';
  /**
   * How many times the larger side was the smaller, or null when nothing
   * traded against it.
   *
   * Null rather than a big number, for the same reason `imbalance()` returns
   * null for an empty book: a level with nothing on the other side of the
   * diagonal is the STRONGEST form of this signal, and any figure printed for
   * it — infinity, a cap, a large quotient — is a measurement nobody made. A
   * reader who sees `99x` believes 99 was counted.
   */
  readonly ratio: number | null;
}

export interface FootprintBar {
  /** Bar open, epoch seconds — the same grid the candles use. */
  readonly time: number;
  /** Descending in price, so a renderer draws top to bottom without sorting. */
  readonly levels: readonly FootprintLevel[];
  readonly bidVolume: number;
  readonly askVolume: number;
  readonly delta: number;
  readonly total: number;
  /**
   * Prints that could not be assigned to a side.
   *
   * Reported rather than dropped or split. A bar whose volume is mostly
   * unclassified is a bar whose delta means nothing, and the reader has to be
   * able to see that.
   */
  readonly unclassified: number;
  /**
   * Price with the most volume in this bar, or null when nothing classified.
   *
   * Null rather than the first price: "the point of control is the top of the
   * bar" is a statement a trader would act on, and it would be an artefact of
   * an empty bar rather than a fact about the market.
   */
  readonly pointOfControl: string | null;
  readonly imbalances: readonly FootprintImbalance[];
}

export interface FootprintOptions {
  /**
   * Bar opens in epoch seconds, ascending — the chart's own grid.
   *
   * Required rather than derived. See decision 4 in the header: a derived grid
   * agrees with a session-aligned chart only by luck.
   */
  readonly barTimes: readonly number[];
  /**
   * Bar length in seconds.
   *
   * Bounds the last bar, and closes the gap between sessions: a print after a
   * bar's open but more than one bar length past it belongs to no bar on this
   * chart and is dropped rather than back-dated into the one before it.
   */
  readonly bucketSec: number;
  /** From the instrument master, never hard-coded. */
  readonly tickSize: Decimal;
  /** Decimals to render a price at, also from the master. */
  readonly precision: number;
  /**
   * How many ticks one row spans. One by default.
   *
   * The diagonal follows it: with four ticks to a row, an imbalance compares a
   * row against the row below, which is four ticks down. Anything else would
   * compare a row against part of itself.
   */
  readonly ticksPerRow?: number;
  /**
   * How many times one side must exceed the other to be an imbalance.
   *
   * Three is the platform convention. Exposed because it is a reading
   * preference rather than a fact — a trader who wants four should get four,
   * and AEGIS should not have a house number.
   */
  readonly imbalanceRatio?: number;
  /**
   * Ignore a diagonal pair whose larger side is below this.
   *
   * Without it, one contract against zero is an infinite ratio and every thin
   * level at the edge of a bar lights up — which is how a footprint ends up
   * covered in marks that mean nothing.
   */
  readonly minImbalanceSize?: number;
}

export const DEFAULT_IMBALANCE_RATIO = 3;
export const DEFAULT_MIN_IMBALANCE_SIZE = 5;

/**
 * The footprint row a price belongs to, as a fixed-precision string.
 *
 * Deliberately not the `snapToTick` in `instruments/math.js`, which rounds to
 * the NEAREST tick. Rounding is right when you are correcting a price; it is
 * wrong when you are bucketing one, because a print half a tick under the
 * bar's high would round UP into a row at a price that never traded — a level
 * a trader reads as "it printed there". Flooring can only ever assign a print
 * to a row at or below where it happened, and every row lands on an exact
 * multiple of the tick size, so two clients bucketing the same tape agree row
 * for row.
 *
 * The string is the row's identity as well as its label: comparing floats to
 * group prices is how `4249.999999999999` becomes its own price level.
 */
export function priceRow(price: number, rowSize: Decimal, precision: number): string {
  if (rowSize.lte(0)) return price.toFixed(precision);
  const rows = dec(price).div(rowSize).floor();
  return rows.times(rowSize).toFixed(precision);
}

/**
 * The height of one row, in price.
 *
 * The instrument's tick size times the grouping. Derived in one place because
 * three things have to agree about it — where a print buckets, which row is
 * "one below" for an imbalance, and how far apart the axis draws its rows —
 * and a footprint where those disagree is one where the marks are on the wrong
 * rows and nothing about the picture says so.
 */
export function rowSize(tickSize: Decimal, ticksPerRow: number | undefined): Decimal {
  return tickSize.times(Math.max(1, Math.floor(ticksPerRow ?? 1)));
}

/**
 * The bar a print belongs to, or null when it belongs to none of them.
 *
 * Binary search over the caller's bar opens rather than arithmetic on the
 * timestamp — the grid is whatever the chart is drawing, including the jump
 * across a session break, and arithmetic cannot know where those are.
 *
 * Null for a print before the first bar or past the end of the last one.
 * Dropping it is the honest outcome: the alternative is a column at a time no
 * candle occupies, which a reader would take for a bar the chart failed to
 * draw.
 */
function bucketOf(
  atMs: number, barTimes: readonly number[], bucketSec: number,
): number | null {
  const at = Math.floor(atMs / 1000);
  if (barTimes.length === 0 || at < barTimes[0]!) return null;

  let lo = 0;
  let hi = barTimes.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (barTimes[mid]! <= at) lo = mid; else hi = mid - 1;
  }
  const open = barTimes[lo]!;
  return at < open + bucketSec ? open : null;
}

/**
 * Build footprint bars from a window of prints.
 *
 * Pure over its inputs. The prints are expected in any order — a tape read
 * from two sources, or replayed, is not reliably sorted — so bucketing is by
 * timestamp rather than by position.
 *
 * Returns a column only for bars that actually had prints. A bar with no tape
 * is not the same as a bar in which nothing traded, and emitting an empty
 * column for it would assert the second. A renderer aligns columns to candles
 * by `time`, so a missing one draws as a gap, which is what it is.
 */
export function buildFootprint(
  prints: readonly TapePrint[], opts: FootprintOptions,
): readonly FootprintBar[] {
  const ratio = opts.imbalanceRatio ?? DEFAULT_IMBALANCE_RATIO;
  const minSize = opts.minImbalanceSize ?? DEFAULT_MIN_IMBALANCE_SIZE;
  const bucketSec = Math.max(1, Math.floor(opts.bucketSec));
  // Sorted defensively: the caller's bar series is ascending everywhere in this
  // codebase, but the binary search below silently returns the wrong bar rather
  // than failing if it ever is not.
  const barTimes = [...opts.barTimes].sort((a, b) => a - b);
  const row = rowSize(opts.tickSize, opts.ticksPerRow);

  interface Acc { bid: number; ask: number }
  const bars = new Map<number, { levels: Map<string, Acc>; unclassified: number }>();

  for (const p of prints) {
    if (!Number.isFinite(p.price) || !Number.isFinite(p.size) || p.size <= 0) continue;

    const time = bucketOf(p.at, barTimes, bucketSec);
    if (time === null) continue;
    let bar = bars.get(time);
    if (!bar) { bar = { levels: new Map(), unclassified: 0 }; bars.set(time, bar); }

    if (p.aggressor === 'unknown') {
      // Counted, never assigned. See the header — delta is the figure this
      // chart is read for, and a guess here is a number nobody measured.
      bar.unclassified += p.size;
      continue;
    }

    const price = priceRow(p.price, row, opts.precision);
    const level = bar.levels.get(price) ?? { bid: 0, ask: 0 };
    if (p.aggressor === 'buy') level.ask += p.size; else level.bid += p.size;
    bar.levels.set(price, level);
  }

  return [...bars.entries()]
    .sort(([a], [b]) => a - b)
    .map(([time, bar]) => finishBar(time, bar, opts, row, ratio, minSize));
}

function finishBar(
  time: number,
  bar: { levels: Map<string, { bid: number; ask: number }>; unclassified: number },
  opts: FootprintOptions,
  row: Decimal,
  ratio: number,
  minSize: number,
): FootprintBar {
  const levels: FootprintLevel[] = [...bar.levels.entries()]
    .map(([price, v]) => ({
      price,
      bidVolume: v.bid,
      askVolume: v.ask,
      delta: v.ask - v.bid,
      total: v.ask + v.bid,
    }))
    // Descending, so a renderer draws top to bottom without sorting.
    .sort((a, b) => Number(b.price) - Number(a.price));

  const askVolume = levels.reduce((s, l) => s + l.askVolume, 0);
  const bidVolume = levels.reduce((s, l) => s + l.bidVolume, 0);

  return {
    time,
    levels,
    bidVolume,
    askVolume,
    delta: askVolume - bidVolume,
    total: askVolume + bidVolume,
    unclassified: bar.unclassified,
    pointOfControl: pointOfControl(levels),
    imbalances: findImbalances(levels, opts, row, ratio, minSize),
  };
}

/**
 * The price that traded the most in a bar.
 *
 * Ties resolve to the LOWER price, deterministically. Not because the lower one
 * is more meaningful, but because a point of control that moves between two
 * equally-traded prices on every re-render is a mark a trader would read as
 * the market changing its mind.
 */
function pointOfControl(levels: readonly FootprintLevel[]): string | null {
  // No zero-volume guard: `finishBar` only ever creates a row from a print
  // that was classified and sized, so a row with no volume cannot exist. A
  // guard for it would be untestable through this module's surface, and an
  // untestable branch is one nobody can prove still works.
  let best: FootprintLevel | null = null;
  for (const l of levels) {
    if (best === null
      || l.total > best.total
      || (l.total === best.total && Number(l.price) < Number(best.price))) {
      best = l;
    }
  }
  return best?.price ?? null;
}

/**
 * Diagonal imbalances.
 *
 * Ask volume at P against bid volume in the row below, which is the pair of
 * orders that actually competed. See the header — comparing the two sides at
 * the same price is the mistake this function exists to not make.
 *
 * "One below" is one ROW, not one tick: with four ticks to a row those are
 * different prices, and using the tick would compare a row against part of
 * itself and find nothing.
 */
function findImbalances(
  levels: readonly FootprintLevel[],
  opts: FootprintOptions,
  row: Decimal,
  ratio: number,
  minSize: number,
): readonly FootprintImbalance[] {
  const byPrice = new Map(levels.map((l) => [l.price, l]));
  const out: FootprintImbalance[] = [];

  for (const level of levels) {
    const below = dec(level.price).minus(row).toFixed(opts.precision);
    const under = byPrice.get(below);
    if (!under) continue;

    // Buyers lifting at P against sellers hitting at P - 1 tick.
    const buy = level.askVolume;
    const sell = under.bidVolume;

    // `buy > sell` is not implied by the ratio test: a reader who sets the
    // ratio to 1 is asking for "any lead at all", and without the strict
    // comparison an even pair would satisfy `buy >= sell * 1` and be marked on
    // both sides of the same diagonal.
    if (buy >= minSize && buy >= sell * ratio && buy > sell) {
      out.push({ price: level.price, side: 'buy', ratio: ratioOf(buy, sell) });
    } else if (sell >= minSize && sell >= buy * ratio && sell > buy) {
      out.push({ price: below, side: 'sell', ratio: ratioOf(sell, buy) });
    }
  }
  return out;
}

/**
 * How many times the larger side was the smaller.
 *
 * Null when there was no smaller side. Not capped otherwise: a genuine 40x is
 * a fact about the bar, and rounding it down to a house maximum would make two
 * different levels read the same.
 */
function ratioOf(larger: number, smaller: number): number | null {
  if (smaller <= 0) return null;
  return Math.round((larger / smaller) * 10) / 10;
}

/**
 * Delta accumulated across bars, in order.
 *
 * The session-level read: a market that grinds up while cumulative delta falls
 * is being sold into every tick of the move, which is a fact about the tape
 * that a candle chart cannot show.
 *
 * Starts at zero each call rather than carrying a session total, because
 * "since when" is the caller's decision and a hidden origin makes the number
 * unreadable.
 */
export function cumulativeDelta(
  bars: readonly FootprintBar[],
): readonly { readonly time: number; readonly delta: number; readonly cumulative: number }[] {
  let running = 0;
  return bars.map((b) => {
    running += b.delta;
    return { time: b.time, delta: b.delta, cumulative: running };
  });
}

/**
 * What fraction of a bar's volume could not be classified.
 *
 * A renderer uses this to say so. Above about a third, delta is not a reading
 * of anything and the chart should stop implying that it is.
 */
export function unclassifiedShare(bar: FootprintBar): number {
  const all = bar.total + bar.unclassified;
  return all === 0 ? 0 : bar.unclassified / all;
}

/** Beyond this, a bar's delta is not worth drawing as a conclusion. */
export const UNREADABLE_ABOVE = 1 / 3;
